import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Per-route SEO: dynamic <title>, meta description, canonical URL and OG title/description.
// Minimal, dependency-free alternative to react-helmet. Keeps SEO signals aligned with route.

const BASE_TITLE = "Converged Security Institute";
const DEFAULT_DESC = "Converged Security Institute (CSI) - professional qualifications in converged security leadership. CSCE, CSP, CSL, CCSO and the PGDCSL Postgraduate Executive Diploma.";

const ROUTE_META = {
  "/":              { title: "Converged Security Institute - CSCE · CSP · CSL · CCSO · PGDCSL", desc: DEFAULT_DESC },
  "/education":     { title: "CSI Qualification Pathway & Certification Policy", desc: "A structured qualification pathway from converged security foundations to postgraduate executive leadership - governed by the CSI Certification Policy." },
  "/courses":       { title: "CSI Programmes - CSCE, CSP, CSL, CCSO, PGDCSL", desc: "Browse Converged Security Institute programmes: CSCE, CSP, CSL (chartered), CCSO (chartered) and the PGDCSL Postgraduate Executive Diploma." },
  "/instructors":   { title: "CSI Faculty - Prof. Dr. Vladimir Bunic, Chief Instructor & Managing Director", desc: "Meet the Converged Security Institute faculty led by Prof. Dr. Vladimir Bunic - Chief Instructor & Managing Director, Distinguished Professor in IT & Cyber Security." },
  "/policies":      { title: "CSI Policies & Programme & Governance Manual (PGDCSL v1.0)", desc: "All CSI regulations and policies that make up the PGDCSL Programme & Governance Manual - qualification policy, assessment, governance, CPD, academic integrity and appeals." },
  "/publications":  { title: "CSI Publications & CSF Standards", desc: "Research papers, policy briefs, frameworks and articles advancing converged security - including the full GCSB Converged Security Framework (CSF) suite (Version 2.0) of published standards and policies." },
  "/live":          { title: "CSI Live Cohorts & Sessions", desc: "Upcoming CSI live cohort sessions - delivered by faculty across the CSCE, CSP and PGDCSL pathways." },
  "/login":         { title: "Log in · CSI", desc: "Sign in to your Converged Security Institute candidate account." },
  "/register":      { title: "Join CSI - create your candidate account", desc: "Create your Converged Security Institute account to enrol on programmes, track CPD and manage your qualification pathway." },
  "/dashboard":     { title: "Candidate Dashboard · CSI", desc: "Your CSI candidate dashboard - enrolment, progress and certificates." },
  "/cpd":           { title: "CPD Tracker · CSI", desc: "Log Continuing Professional Development hours and maintain active standing for your CSI designation." },
};

function set(name, value, attr = "name") {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function RouteSEO() {
  const location = useLocation();

  useEffect(() => {
    // Normalise trailing slash so '/instructors/' and '/instructors' resolve to the same meta entry
    const raw = location.pathname || "/";
    const path = raw.length > 1 ? raw.replace(/\/$/, "") : raw;
    if (path.startsWith("/courses/")) return; // CourseDetailPage sets its own SEO
    // Match exact path first, then prefix match for dynamic routes
    let meta = ROUTE_META[path];
    if (!meta) {
      if (path.startsWith("/courses/"))      meta = { title: "CSI Programme detail", desc: "Programme details, pricing, assessment and award on the Converged Security Institute pathway." };
      else if (path.startsWith("/learn/"))   meta = { title: "Lesson · CSI", desc: "CSI lesson player." };
    }
    const title = meta?.title ? `${meta.title}` : BASE_TITLE;
    const desc  = meta?.desc  || DEFAULT_DESC;
    const url   = `${window.location.origin}${path}`;

    if (document.title !== title) document.title = title;
    set("description", desc);
    set("og:title", title, "property");
    set("og:description", desc, "property");
    set("og:url", url, "property");
    set("twitter:title", title);
    set("twitter:description", desc);
    setLink("canonical", url);
  }, [location.pathname]);

  return null;
}
